import React, { useMemo, useRef, useState, useEffect } from "react";
import TopRightSemiCircular from "../assets/img/common/semi-circular-top-right.png";
import LeftSemiCircular from "../assets/img/common/semi-cicular-left.png";
import { ReactComponent as SearchIcon } from "../assets/img/common/search-icon.svg";
import Container from "../components/common/Container";
import { Bars, Oval } from "react-loader-spinner";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectGeneral, setShowAuthModal } from "../redux/slices/generalSlice";
import { FiFilter, FiPlus, FiArrowRight, FiInfo } from "react-icons/fi";
import { HiOutlineSparkles, HiUserGroup, HiSwitchHorizontal } from "react-icons/hi";
import { BsPeople, BsCollection, BsArrowRight } from "react-icons/bs";
import MetaTags from "../components/common/MetaTags";
import CommunityCard from "../components/communities/CommunityCard";
import CommunityFilters from "../components/communities/CommunityFilters";
import Pagination from "../components/common/Pagination";
import {
  useGetCommunitiesQuery,
  useGetCommunityCategoriesQuery,
  useGetFeaturedCommunitiesQuery,
} from "../redux/slices/api/communityApiSlice";
import { useLazyGetUserQuery } from "../redux/slices/api/userApi";

// Category display labels
const categoryLabels = {
  desci_general: "DeSci General",
  biotech_life_sciences: "Biotech & Life Sciences",
  ai_computer_science: "AI & Computer Science",
  open_source_software: "Open Source",
  women_in_science: "Women in Science",
  regional: "Regional",
  climate_environment: "Climate & Environment",
  space_astronomy: "Space & Astronomy",
  other: "Other",
};

const Communities = () => {
  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");

  const searchTimeoutRef = useRef(null);
  const dispatch = useDispatch();

  // Redux state
  const { isLoggedIn } = useSelector(selectGeneral);

  // Get user data to check role
  const [triggerGetUser, { data: userData }] = useLazyGetUserQuery();

  useEffect(() => {
    if (isLoggedIn) {
      triggerGetUser();
    }
  }, [isLoggedIn, triggerGetUser]);

  // Both individuals and organizations can create communities now
  const userRole = userData?.user?.role;
  const canCreateCommunity = isLoggedIn && userRole && ['applicant', 'organization'].includes(userRole);
  const hasCompletedProfile = isLoggedIn && userRole && userRole !== 'un_registered';

  // API queries
  const { data: communitiesData, isLoading } = useGetCommunitiesQuery({
    search: searchValue,
    category: selectedCategory,
    page: currentPage,
    limit: 12,
  });

  // Featured communities query - comment out for now
  // const { data: featuredCommunities, isLoading: featuredLoading } =
  //   useGetFeaturedCommunitiesQuery();

  const { data: categories } = useGetCommunityCategoriesQuery();

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchValue(value);
      setCurrentPage(1);
    }, 500);
  };

  // Handle category filter
  const handleCategoryClick = (categoryValue) => {
    if (selectedCategory === categoryValue) {
      setSelectedCategory("");
    } else {
      setSelectedCategory(categoryValue);
    }
    setCurrentPage(1);
  };

  // Get communities and pagination
  const communities = communitiesData?.communities || [];
  const pagination = communitiesData?.pagination || { total: 0, pages: 1 };

  if (isLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Bars
          height="50"
          width="50"
          color="#8538ff"
          ariaLabel="bars-loading"
          visible={true}
        />
      </div>
    );
  }

  return (
    <div
      className="w-full flex justify-center bg-no-repeat roboto-font"
      style={{
        backgroundImage: `url(${TopRightSemiCircular}), url(${LeftSemiCircular})`,
        backgroundPosition: "top right, left top",
      }}
    >
      <MetaTags
        title="Communities"
        description="Join DeSci communities, collect loyalty NFTs, and unlock exclusive perks and benefits"
        link="/communities"
      />
      <Container className="flex flex-col items-center pt-28 pb-10">
        {/* Hero Section */}
        <div className="w-full mb-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3 abel-font bg-gradient-to-r from-[#8538ff] to-[#7e7cff] bg-clip-text text-transparent">
            Web3 Communities
          </h1>
          <p className="text-base text-gray-600 max-w-xl mx-auto">
            Join exclusive research communities, collect loyalty NFTs, and unlock special perks
          </p>
        </div>

        {/* Create Community Banner - Contextual based on user state */}
        <div className="w-full mb-8">
          {isLoggedIn && canCreateCommunity ? (
            // Logged in with completed profile - Show create button
            <div className="w-full bg-gradient-to-r from-[#8538ff] via-[#9c5cff] to-[#7e7cff] rounded-2xl p-6 shadow-lg">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-white">
                  <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                    <HiUserGroup className="text-2xl" />
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-xl font-bold">Build Your Community</h3>
                    <p className="text-white/80 text-sm">Create loyalty NFTs and grow your supporter base</p>
                  </div>
                </div>
                <Link
                  to="/create-community"
                  className="flex items-center gap-2 px-6 py-3 bg-white text-[#8538ff] font-semibold rounded-xl hover:bg-white/90 transition-all shadow-md hover:shadow-lg"
                >
                  <FiPlus className="text-lg" />
                  Create Community
                  <FiArrowRight className="text-lg" />
                </Link>
              </div>
            </div>
          ) : isLoggedIn && !hasCompletedProfile ? (
            // Logged in but incomplete profile - Guide to complete profile
            <div className="w-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                    <FiInfo className="text-2xl text-amber-600" />
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-lg font-bold text-gray-800">Complete Your Profile</h3>
                    <p className="text-gray-600 text-sm">Finish setting up your profile to create and manage communities</p>
                  </div>
                </div>
                <Link
                  to="/create-profile"
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-xl hover:opacity-90 transition-all shadow-md"
                >
                  <HiSwitchHorizontal className="text-lg" />
                  Complete Profile
                </Link>
              </div>
            </div>
          ) : (
            // Not logged in - Guide to sign up/login
            <div className="w-full bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-2xl p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                    <HiUserGroup className="text-2xl text-gray-500" />
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-lg font-bold text-gray-800">Create Your Own Community</h3>
                    <p className="text-gray-600 text-sm">Sign up to build and grow your research community</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 hidden sm:block">Already have an account?</span>
                  <button
                    onClick={() => {
                      dispatch(setShowAuthModal({ show: true, redirect: '/create-community' }));
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white font-medium rounded-xl hover:opacity-90 transition-all shadow-md"
                  >
                    Sign In
                    <BsArrowRight />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Featured Communities Carousel - Commented out for now */}
        {/* {featuredCommunities?.length > 0 && (
          <div className="w-full mb-12">
            <div className="flex items-center gap-2 mb-6">
              <HiOutlineSparkles className="text-2xl text-purple-500" />
              <h2 className="text-2xl font-bold text-gray-800">Featured Communities</h2>
            </div>
            <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
              {featuredCommunities.slice(0, 4).map((community) => (
                <div key={community._id} className="flex-shrink-0">
                  <CommunityCard data={community} isSlide />
                </div>
              ))}
            </div>
          </div>
        )} */}

        {/* Main Content */}
        <div className="w-full flex gap-6 relative">
          {/* Sidebar Filters - Compact */}
          <aside className="min-w-[220px] w-[22%] max-w-[260px] hidden lg:flex flex-col gap-3 pb-10">
            <div className="w-full flex flex-col sticky top-24 gap-3">
              {/* Search */}
              <div className="w-full bg-white rounded-xl p-3 shadow-md">
                <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2 focus-within:border-primary transition-colors">
                  <SearchIcon className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={handleSearchChange}
                    className="w-full ml-2 outline-none text-sm"
                  />
                </div>
              </div>

              {/* Categories */}
              <div className="w-full max-h-[280px] flex flex-col rounded-xl bg-white p-4 shadow-md">
                <h3 className="text-sm font-bold mb-3 text-gray-700 uppercase tracking-wide">
                  Categories
                </h3>
                <div className="flex flex-col gap-1.5 overflow-auto custom-primary-tiny-scroll">
                  {(categories || []).map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => handleCategoryClick(cat.value)}
                      className={`w-full flex justify-between items-center px-3 py-2 rounded-lg text-xs transition-all ${
                        selectedCategory === cat.value
                          ? "bg-primary text-white"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <span className="truncate">{cat.label}</span>
                      <span className="opacity-70 ml-1">({cat.count})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Compact Stats */}
              <div className="w-full bg-gradient-to-br from-[#8538ff] to-[#7e7cff] rounded-xl p-4 text-white shadow-md">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 opacity-90">
                      <BsPeople className="text-xs" />
                      Communities
                    </span>
                    <span className="font-bold">{pagination.total}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 opacity-90">
                      <BsCollection className="text-xs" />
                      NFTs
                    </span>
                    <span className="font-bold">--</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Communities Grid */}
          <main className="flex-1">
            {/* Mobile Filter Button */}
            <div className="lg:hidden flex justify-between items-center mb-6">
              <span className="text-gray-600">
                {pagination.total} communities found
              </span>
              <button
                onClick={() => setShowFiltersPopup(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <FiFilter />
                Filters
              </button>
            </div>

            {/* Mobile Search */}
            <div className="lg:hidden mb-6">
              <div className="flex items-center bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
                <SearchIcon className="w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search communities..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  className="w-full ml-3 outline-none text-sm"
                />
              </div>
            </div>

            {/* Results Header */}
            <div className="hidden lg:flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                {selectedCategory
                  ? categoryLabels[selectedCategory]
                  : "All Communities"}
              </h2>
              <span className="text-gray-600">
                {pagination.total} communities
              </span>
            </div>

            {/* Communities Grid */}
            {communities.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {communities.map((community) => (
                    <CommunityCard key={community._id} data={community} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="mt-10 flex justify-center">
                    <Pagination
                      currentPage={currentPage}
                      totalCount={pagination.total}
                      pageSize={12}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <BsPeople className="text-6xl mb-4 opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No communities found</h3>
                <p className="text-center max-w-md">
                  {searchValue
                    ? "Try adjusting your search or filters"
                    : "Be the first to create a community!"}
                </p>
                {canCreateCommunity && (
                  <Link
                    to="/create-community"
                    className="mt-6 px-6 py-3 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity"
                  >
                    Create Community
                  </Link>
                )}
              </div>
            )}
          </main>
        </div>

        {/* Mobile Filters Popup */}
        {showFiltersPopup && (
          <CommunityFilters
            isMobile
            onClose={() => setShowFiltersPopup(false)}
            filter={{ category: selectedCategory ? [selectedCategory] : [] }}
            handleChangeFilter={({ category }) => {
              if (category) handleCategoryClick(category);
            }}
            handleRemoveFilter={({ category }) => {
              if (category === selectedCategory) setSelectedCategory("");
            }}
          />
        )}
      </Container>
    </div>
  );
};

export default Communities;
